/**
 * Bundled copies of the HiiKER style sprite's pucks for POI types newer than some
 * styles' sprites (a style cached or downloaded before they were added). The
 * sprite carries the same art; these only stand in where it is missing, so the
 * POI pins never draw blank. They are added only when Mapbox reports a puck
 * missing — see ensureLocalPuckFallbacks for why never sooner.
 */

import puck_icon_defibrillator from '../../../images/pois/puck_icon_defibrillator.png';
import puck_icon_laundrette from '../../../images/pois/puck_icon_laundrette.png';

export const LOCAL_PUCK_FALLBACKS: Record<string, string> = {
  puck_icon_defibrillator,
  puck_icon_laundrette,
};

/** The bundled pucks are the sprite's @2x images, so they draw at the sprite's size. */
export const LOCAL_PUCK_PIXEL_RATIO = 2;

/**
 * Adds `url` to the map as image `name` unless the style already has one by that name.
 * Never rejects: callers fire it without awaiting, and a fallback icon must not be able
 * to break the layer it is for.
 */
export function ensureLocalImage(map: any, name: string, url: string, pixelRatio = 1): Promise<void> {
  if (!map || typeof map.loadImage !== 'function' || map.hasImage?.(name)) return Promise.resolve();

  return new Promise((resolve) => {
    try {
      map.loadImage(url, (error: unknown, image: unknown) => {
        if (!error && image && !map.hasImage(name)) map.addImage(name, image, { pixelRatio });
        resolve();
      });
    } catch {
      resolve();
    }
  });
}

const mapsWithFallback = new WeakSet<object>();

/**
 * Adds a bundled puck the moment the style asks for one it does not have.
 *
 * Only on `styleimagemissing`, never on a `hasImage` check up front. Until the
 * style's sprite has downloaded, hasImage is false for every sprite image, so on
 * a cold load (first visit, cleared or expired cache) the small bundled PNG got in
 * first. Mapbox then rejected the sprite at the duplicate name ("An image with the
 * name … already exists") and added none of its other images: the waypoint icons
 * and most basemap icons went missing. Mapbox fires styleimagemissing only once
 * the sprite has loaded, so the sprite always goes in whole and these stand in
 * only where it really lacks the puck. The listener survives style switches, so
 * it is attached once per map.
 */
export function ensureLocalPuckFallbacks(map: any): void {
  if (!map || typeof map.on !== 'function' || mapsWithFallback.has(map)) return;
  mapsWithFallback.add(map);
  map.on('styleimagemissing', (event: { id?: unknown }) => {
    const name = typeof event?.id === 'string' ? event.id : '';
    if (!Object.prototype.hasOwnProperty.call(LOCAL_PUCK_FALLBACKS, name)) return;
    void ensureLocalImage(map, name, LOCAL_PUCK_FALLBACKS[name], LOCAL_PUCK_PIXEL_RATIO);
  });
}
